(ns practitest.frontend.components2.dashboard.charts.marks-gauge
  (:require [practitest.frontend.components2.dashboard.svg :as svg]
            ["d3" :as d3]))

(def ^:private reference-radius 110)

(def defaults
  "Arc geometry, in units of reference-radius."
  (let [start-angle (- (* Math/PI 0.8))]
    {:weight-ratio        0.14
     :start-angle         start-angle
     :end-angle           (- start-angle)
     :transition-duration 600
     :scale-label-offset  20
     :badge-padding       8
     :badge-height        20
     :badge-radius        5
     :tick-count          nil
     :tick-length         6
     :tick-padding        6
     :tick-inset          0.45}))

(defn- arc [{:keys [start-angle end-angle radius weight]}]
  {:from     [:t 0]
   :to       [:t 1]
   :scale    [start-angle end-angle]
   :radius   radius
   :weight   weight
   :corner-r (* weight 0.5)})

(defn- tick [{:keys [tick-length tick-padding tick-inset
                     start-angle end-angle radius weight]}]
  {:class  "tick"
   :scale  [(+ start-angle tick-inset) (- end-angle tick-inset)]
   :offset (- radius weight tick-padding)
   :length (- tick-length)})

(defn- scale-labels-view
  "Labels under the start and end of the arc, centered on the arc band."
  [[start-label end-label] {:keys [start-angle end-angle radius weight scale-label-offset]}]
  (let [r (- radius (/ weight 2))]
    (for [[angle text] [[start-angle start-label] [end-angle end-label]]
          :let [[x y] (svg/angle->xy angle r)]]
      [:text.scale-label {:x x :y (+ y scale-label-offset)}
       text])))

(defn- status-badge [text {:keys [badge-padding badge-height badge-radius]}]
  [:svg/box {:class     "badge-bg"
             :padding-x badge-padding
             :height    badge-height
             :rx        badge-radius}
   [:text.badge-text text]])

(defn- gauge-view
  [{:keys [display-value not-available? label icon level status-label scale-labels]}
   value
   animate?
   {:keys [tick-count transition-duration] :as geometry}]
  (let [arc-bg    (arc geometry)
        arc-value (assoc arc-bg :to [:t (or value 0)])]
    [:<>
     [:g {:class (some-> level name)}
      [:svg/arc.arc-bg arc-bg]
      (if animate?
        [:svg/transition {:duration transition-duration}
         [:svg/arc.arc-value arc-value]]
        [:svg/arc.arc-value arc-value])

      (when tick-count
        (let [tick (tick geometry)]
          (for [i (range (inc tick-count))]
            [:svg/line (merge tick {:angle [:t (/ i tick-count)]})])))

      (if not-available?
        [:text.value-text "N/A"]
        (cond-> [:svg/vstack {:gap 6}
                 [:text.value-text display-value]
                 [:svg/hstack {:gap 4}
                  icon
                  [:text.label-text label]]]
          status-label (conj (status-badge status-label geometry))))]
     (when scale-labels
       (scale-labels-view scale-labels geometry))]))

(defonce ^:private drawn-values (js/WeakMap.))

(defn render!
  "Draws the first datum as a gauge centered in g.marks, scaled to fit
   :outer-radius. Animates the arc only when the value changes."
  [svg mark {:keys [data encode outer-radius]}]
  (let [marks-el (.node (.select svg "g.chart g.marks"))
        datum    (first data)
        value    (get datum (get-in encode [:theta :field]))
        animate? (not= value (.get drawn-values marks-el))
        opts     (merge defaults (dissoc mark :type))
        geometry (assoc opts
                        :radius reference-radius
                        :weight (* reference-radius (:weight-ratio opts)))]
    (.set drawn-values marks-el value)
    (set! (.-innerHTML marks-el) "")
    (let [scaled-g (-> (.select d3 marks-el)
                       (.append "g")
                       (.attr "transform" (str "scale(" (/ outer-radius reference-radius) ")")))]
      (svg/render-hiccup! (.node scaled-g)
                          (gauge-view datum value animate? geometry)
                          {:in-polar? true
                           :view-box  [:svg/polar reference-radius]}))))
